import { showToast } from "../components/toast";
import { createRoot } from "react-dom/client";
import ReactMarkdown from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import remarkGfm from "remark-gfm";
import type { Turn } from "../types";
import { brandingSlug, getBranding } from "../branding";
import { splitProcesso } from "./processo";
import { inlineMermaidAsImages, type MermaidExportFormat } from "./mermaid";

/** Conteúdo entregável de um turn (sem a narração de <processo>). */
function turnAnswer(t: Turn): string {
  return t.role === "assistant" ? splitProcesso(t.content).answer : t.content;
}

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });
  } catch {
    return iso;
  }
}

function turnsToMarkdown(turns: Turn[]): string {
  const lines: string[] = [];
  lines.push(`# Conversa — ${getBranding().appName}`);
  lines.push("");
  lines.push(`_Exportada em ${new Date().toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })} — ${turns.length} mensagens_`);
  lines.push("");
  lines.push("---");
  lines.push("");

  for (const t of turns) {
    const role = t.role === "user" ? "Você" : getBranding().appName;
    lines.push(`## ${role}`);
    lines.push("");
    lines.push(`_${formatTimestamp(t.ts)}_`);
    lines.push("");
    if (t.errored) {
      lines.push(`> ❌ Erro: ${t.errorMessage ?? "não foi possível gerar resposta"}`);
    } else {
      lines.push(turnAnswer(t) || "_(vazio)_");
    }
    if (t.role === "assistant" && t.usage) {
      const total = (t.usage.inputTokens ?? 0) + (t.usage.outputTokens ?? 0);
      const cost = t.costUsd != null ? ` • $${t.costUsd.toFixed(4)}` : "";
      const dur = t.durationMs != null ? ` • ${(t.durationMs / 1000).toFixed(1)}s` : "";
      lines.push("");
      lines.push(`_${total} tokens${cost}${dur}_`);
    }
    lines.push("");
    lines.push("---");
    lines.push("");
  }

  return lines.join("\n");
}

function defaultFilename(ext: string): string {
  const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  return `${brandingSlug()}-conversa-${ts}.${ext}`;
}

export function exportConversationMarkdown(turns: Turn[]): void {
  if (turns.length === 0) {
    showToast("Conversa vazia - nada para exportar.", "error");
    return;
  }
  const md = turnsToMarkdown(turns);
  const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = defaultFilename("md");
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

/**
 * CSS embutido no HTML exportado — autocontido, sem dependência externa.
 * Inspirado no estilo da UI (tema "Solar light") mas adaptado para impressão
 * e leitura em qualquer browser sem precisar dos tokens SCSS.
 */
const EXPORT_HTML_STYLES = `
  /* realce de sintaxe (highlight.js) - mantido na exportação HTML/PDF */
  .hljs-comment, .hljs-quote { color: #8a877f; font-style: italic; }
  .hljs-keyword, .hljs-selector-tag, .hljs-literal, .hljs-type { color: #b3589a; }
  .hljs-string, .hljs-regexp, .hljs-addition { color: #2f9e5c; }
  .hljs-number, .hljs-symbol, .hljs-bullet { color: #c15f3c; }
  .hljs-title, .hljs-section, .hljs-name, .hljs-title.function_ { color: #2f6fed; }
  .hljs-attr, .hljs-attribute, .hljs-variable, .hljs-property { color: #a26b1f; }
  .hljs-built_in, .hljs-title.class_ { color: #0e8a8a; }
  .hljs-meta, .hljs-link { color: #7a6ff0; }
  .hljs-deletion { color: #c0392b; }
  .hljs-strong { font-weight: 700; }

  :root {
    color-scheme: light dark;
    --bg: #faf8f3;
    --surface: #ffffff;
    --surface-alt: #f4f1ea;
    --border: #e8e3da;
    --text: #1f1e1d;
    --text-soft: #4a4843;
    --text-mute: #6b6964;
    --accent: #c15f3c;
    --accent-soft: #f4dccd;
    --user-bg: #f0ede6;
    --code-bg: #f4f1ea;
  }
  @media (prefers-color-scheme: dark) {
    :root {
      --bg: #1a1816;
      --surface: #221f1c;
      --surface-alt: #2a2724;
      --border: #3a3631;
      --text: #ece8df;
      --text-soft: #c8c2b5;
      --text-mute: #908a7e;
      --accent: #e07a52;
      --accent-soft: #4a2e22;
      --user-bg: #2a2724;
      --code-bg: #2a2724;
    }
  }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: var(--bg); color: var(--text); }
  body {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
    font-size: 15px;
    line-height: 1.6;
    padding: 32px 16px 96px;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
  .doc {
    max-width: 760px;
    margin: 0 auto;
    min-width: 0;
  }
  header.doc-header {
    border-bottom: 1px solid var(--border);
    padding-bottom: 16px;
    margin-bottom: 28px;
  }
  header.doc-header h1 {
    font-size: 22px;
    margin: 0 0 6px;
    color: var(--accent);
  }
  header.doc-header .meta {
    color: var(--text-mute);
    font-size: 13px;
  }
  .turn {
    margin-bottom: 22px;
    padding: 16px 20px;
    border-radius: 12px;
    border: 1px solid var(--border);
    background: var(--surface);
    overflow: hidden;
    min-width: 0;
  }
  .turn.user {
    background: var(--user-bg);
  }
  .turn.assistant {
    background: var(--surface);
  }
  .turn-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    margin-bottom: 10px;
    font-size: 12.5px;
  }
  .turn-role {
    font-weight: 600;
    color: var(--accent);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 11px;
  }
  .turn.user .turn-role { color: var(--text-soft); }
  .turn-ts { color: var(--text-mute); font-variant-numeric: tabular-nums; }
  .turn-body { min-width: 0; max-width: 100%; }
  .turn-body p { margin: 0 0 10px; overflow-wrap: anywhere; }
  .turn-body p:last-child { margin-bottom: 0; }
  .turn-body h1, .turn-body h2, .turn-body h3, .turn-body h4 {
    font-weight: 600;
    margin: 16px 0 8px;
    line-height: 1.3;
  }
  .turn-body h1 { font-size: 19px; }
  .turn-body h2 { font-size: 17px; }
  .turn-body h3 { font-size: 15.5px; }
  .turn-body h4 { font-size: 14.5px; }
  .turn-body ul, .turn-body ol { margin: 0 0 10px; padding-left: 22px; }
  .turn-body li { margin: 3px 0; }
  .turn-body pre {
    background: var(--code-bg);
    border: 1px solid var(--border);
    border-radius: 6px;
    padding: 12px 14px;
    overflow-x: auto;
    max-width: 100%;
    font-family: "JetBrains Mono", ui-monospace, "SF Mono", Menlo, Consolas, monospace;
    font-size: 13px;
    line-height: 1.5;
    margin: 10px 0;
    white-space: pre-wrap;
    word-wrap: break-word;
    overflow-wrap: anywhere;
  }
  .turn-body code {
    background: var(--code-bg);
    color: var(--accent);
    padding: 1px 6px;
    border-radius: 3px;
    font-family: "JetBrains Mono", ui-monospace, "SF Mono", Menlo, Consolas, monospace;
    font-size: 0.9em;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
  .turn-body pre code {
    background: transparent;
    padding: 0;
    color: inherit;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
  .turn-body blockquote {
    margin: 10px 0;
    padding: 6px 14px;
    border-left: 3px solid var(--accent-soft);
    color: var(--text-soft);
  }
  .turn-body .table-wrap {
    overflow-x: auto;
    max-width: 100%;
    margin: 12px 0;
  }
  .turn-body table {
    border-collapse: collapse;
    width: 100%;
    font-size: 14px;
    table-layout: auto;
  }
  .turn-body th, .turn-body td {
    border: 1px solid var(--border);
    padding: 7px 10px;
    text-align: left;
    overflow-wrap: anywhere;
  }
  .turn-body th { background: var(--surface-alt); }
  .turn-body a {
    color: var(--accent);
    overflow-wrap: anywhere;
    word-break: break-word;
  }
  .turn-body img { max-width: 100%; height: auto; border-radius: 6px; display: block; }
  .turn-body hr {
    border: 0;
    border-top: 1px solid var(--border);
    margin: 14px 0;
  }
  .turn-footer {
    margin-top: 10px;
    padding-top: 8px;
    border-top: 1px dashed var(--border);
    color: var(--text-mute);
    font-size: 12px;
    font-variant-numeric: tabular-nums;
  }
  .turn-error {
    color: #b34a2c;
    font-weight: 500;
  }
  .doc-footer {
    text-align: center;
    color: var(--text-mute);
    font-size: 12px;
    margin-top: 32px;
    padding-top: 14px;
    border-top: 1px solid var(--border);
  }
  @media print {
    body { padding: 0; background: #fff; }
    .turn { break-inside: avoid; }
    .turn-body pre { break-inside: avoid; }
  }

  /* Diagramas Mermaid (inline SVG) - clicáveis pra abrir em tela cheia. */
  .mermaid-img { cursor: zoom-in; }

  /* Lightbox de diagrama (zoom no scroll do mouse, pan no clique-arrastar) -
     aberto pelo script embutido no fim do documento (ver DLB_SCRIPT). */
  .dlb-overlay {
    position: fixed;
    inset: 0;
    background: rgba(10, 9, 8, 0.88);
    z-index: 9999;
    display: flex;
  }
  .dlb-viewport {
    flex: 1;
    overflow: hidden;
    position: relative;
    cursor: grab;
    touch-action: none;
  }
  .dlb-viewport.dlb-grabbing { cursor: grabbing; }
  .dlb-content {
    position: absolute;
    top: 0;
    left: 0;
    transform-origin: 0 0;
    background: #ffffff;
    padding: 20px;
    border-radius: 8px;
    will-change: transform;
  }
  .dlb-content svg { display: block; max-width: none !important; height: auto; }
  .dlb-close {
    position: fixed;
    top: 16px;
    right: 20px;
    z-index: 10000;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: none;
    background: rgba(255, 255, 255, 0.15);
    color: #fff;
    font-size: 22px;
    line-height: 1;
    cursor: pointer;
  }
  .dlb-close:hover { background: rgba(255, 255, 255, 0.3); }
`;

/**
 * JS plano embutido no HTML exportado: mesmo algoritmo de zoom/pan de
 * frontend/utils/diagramLightbox.ts, reescrito sem imports/módulos porque o
 * arquivo exportado é standalone (sem bundle). Delegado no document (event
 * delegation) pra funcionar mesmo se o conteúdo mudar.
 */
const EXPORT_LIGHTBOX_SCRIPT = `
(function () {
  var MIN_SCALE = 0.5, MAX_SCALE = 8, ZOOM_SPEED = 0.0015;

  function openLightbox(html) {
    var overlay = document.createElement("div");
    overlay.className = "dlb-overlay";
    var closeBtn = document.createElement("button");
    closeBtn.type = "button";
    closeBtn.className = "dlb-close";
    closeBtn.setAttribute("aria-label", "Fechar");
    closeBtn.textContent = "\\u00d7";
    var viewport = document.createElement("div");
    viewport.className = "dlb-viewport";
    var content = document.createElement("div");
    content.className = "dlb-content";
    content.innerHTML = html;
    // o SVG usa width="100%" (relativo ao container); .dlb-content se
    // auto-dimensiona pelo conteúdo, então 100% vira referência circular e o
    // navegador cai no tamanho padrão (~300px). Fixamos width/height a partir
    // do viewBox pra ter tamanho natural determinístico.
    var svgEl = content.querySelector("svg");
    if (svgEl) {
      var vb = svgEl.getAttribute("viewBox");
      var dims = vb ? vb.split(/[\\s,]+/).map(Number) : null;
      var w = dims ? dims[2] : null, h = dims ? dims[3] : null;
      if (w && h && w > 0 && h > 0) {
        svgEl.setAttribute("width", String(w));
        svgEl.setAttribute("height", String(h));
      }
      svgEl.style.maxWidth = "none";
    }
    viewport.appendChild(content);
    overlay.appendChild(closeBtn);
    overlay.appendChild(viewport);
    document.body.appendChild(overlay);
    var prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    var scale = 1, tx = 0, ty = 0, dragging = false, lastX = 0, lastY = 0, moved = false;

    function apply() {
      content.style.transform = "translate(" + tx + "px, " + ty + "px) scale(" + scale + ")";
    }
    function onWheel(e) {
      e.preventDefault();
      var rect = viewport.getBoundingClientRect();
      var cx = e.clientX - rect.left, cy = e.clientY - rect.top;
      var factor = Math.exp(-e.deltaY * ZOOM_SPEED);
      var newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale * factor));
      tx = cx - ((cx - tx) * newScale) / scale;
      ty = cy - ((cy - ty) * newScale) / scale;
      scale = newScale;
      apply();
    }
    function onPointerDown(e) {
      dragging = true;
      moved = false;
      lastX = e.clientX;
      lastY = e.clientY;
      viewport.classList.add("dlb-grabbing");
      viewport.setPointerCapture(e.pointerId);
    }
    function onPointerMove(e) {
      if (!dragging) return;
      tx += e.clientX - lastX;
      ty += e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      moved = true;
      apply();
    }
    function onPointerUp(e) {
      dragging = false;
      viewport.classList.remove("dlb-grabbing");
      try { viewport.releasePointerCapture(e.pointerId); } catch (err) {}
    }
    function onKeydown(e) {
      if (e.key === "Escape") close();
    }
    function close() {
      document.removeEventListener("keydown", onKeydown);
      document.body.style.overflow = prevOverflow;
      overlay.remove();
    }

    overlay.addEventListener("click", function (e) {
      if ((e.target === overlay || e.target === viewport) && !moved) close();
    });
    closeBtn.addEventListener("click", close);
    viewport.addEventListener("wheel", onWheel, { passive: false });
    viewport.addEventListener("pointerdown", onPointerDown);
    viewport.addEventListener("pointermove", onPointerMove);
    viewport.addEventListener("pointerup", onPointerUp);
    viewport.addEventListener("pointerleave", onPointerUp);
    document.addEventListener("keydown", onKeydown);

    requestAnimationFrame(function () {
      var vRect = viewport.getBoundingClientRect();
      var cRect = content.getBoundingClientRect();
      var fit = Math.min(1, (vRect.width * 0.92) / cRect.width, (vRect.height * 0.92) / cRect.height);
      scale = Math.max(MIN_SCALE, fit);
      tx = (vRect.width - cRect.width * scale) / 2;
      ty = (vRect.height - cRect.height * scale) / 2;
      apply();
    });
  }

  document.addEventListener("click", function (e) {
    var target = e.target && e.target.closest ? e.target.closest(".mermaid-img") : null;
    if (!target) return;
    var svg = target.querySelector("svg");
    openLightbox(svg ? svg.outerHTML : target.innerHTML);
  });
})();
`;

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Renderiza markdown via React num container offscreen e devolve o HTML resultante.
 * Usa o mesmo `ReactMarkdown` da UI pra manter consistência.
 */
async function renderMarkdownToHtml(
  md: string,
  mermaidFormat: MermaidExportFormat = "png",
): Promise<string> {
  const host = document.createElement("div");
  host.style.position = "fixed";
  host.style.top = "0";
  host.style.left = "0";
  host.style.width = "0";
  host.style.height = "0";
  host.style.overflow = "hidden";
  document.body.appendChild(host);

  const reactTarget = document.createElement("div");
  host.appendChild(reactTarget);
  const root = createRoot(reactTarget);

  let renderedHtml: string;
  try {
    root.render(<ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[[rehypeHighlight, { detect: false, plainText: ["mermaid", "txt", "text"] }]]}>{md}</ReactMarkdown>);
    // aguarda 2 frames pra garantir que o React montou
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
    );
    renderedHtml = reactTarget.innerHTML;
  } finally {
    // desmonta e remove ANTES de qualquer mutação manual no HTML capturado.
    // inlineMermaidAsImages troca nós no DOM diretamente - se mexesse na
    // árvore que o React ainda gerencia, o unmount() tentaria remover nós que
    // já não existem mais ali ("Failed to execute 'removeChild' on 'Node'").
    root.unmount();
    host.remove();
  }

  // árvore solta, fora do controle do React - segura pra mutação manual.
  const target = document.createElement("div");
  target.innerHTML = renderedHtml;

  // Wrappa tabelas num container scrollável horizontal pra evitar que tabelas
  // largas estourem a largura do bloco de mensagem.
  for (const table of Array.from(target.querySelectorAll("table"))) {
    if (table.parentElement?.classList.contains("table-wrap")) continue;
    const wrap = document.createElement("div");
    wrap.className = "table-wrap";
    table.parentNode?.insertBefore(wrap, table);
    wrap.appendChild(table);
  }
  // Converte diagramas Mermaid em imagem (SVG inline p/ HTML, PNG p/ PDF) —
  // como texto de código eles ficariam ilegíveis no documento exportado.
  await inlineMermaidAsImages(target, mermaidFormat);
  return target.innerHTML;
}

function renderTurnBlock(t: Turn, bodyHtml: string): string {
  const role = t.role === "user" ? "Você" : getBranding().appName;
  const roleClass = t.role === "user" ? "user" : "assistant";
  const ts = formatTimestamp(t.ts);

  let footer = "";
  if (t.role === "assistant" && (t.usage || t.costUsd != null || t.durationMs != null)) {
    const parts: string[] = [];
    if (t.usage) {
      const tot = (t.usage.inputTokens ?? 0) + (t.usage.outputTokens ?? 0);
      parts.push(`${tot.toLocaleString("pt-BR")} tokens`);
    }
    if (t.costUsd != null) parts.push(`$${t.costUsd.toFixed(4)}`);
    if (t.durationMs != null) parts.push(`${(t.durationMs / 1000).toFixed(1)}s`);
    if (t.aborted) parts.push("interrompida");
    if (parts.length > 0) {
      footer = `<div class="turn-footer">${parts.map(escapeHtml).join(" • ")}</div>`;
    }
  }
  return `<article class="turn ${roleClass}">
  <div class="turn-head">
    <span class="turn-role">${escapeHtml(role)}</span>
    <span class="turn-ts">${escapeHtml(ts)}</span>
  </div>
  <div class="turn-body">${bodyHtml}</div>
  ${footer}
</article>`;
}

async function renderTurnBody(t: Turn): Promise<string> {
  if (t.errored) {
    return `<p class="turn-error">❌ ${escapeHtml(t.errorMessage ?? "Erro ao gerar resposta.")}</p>`;
  }
  const content = turnAnswer(t);
  if (!content.trim()) {
    return `<p><em>(vazio)</em></p>`;
  }
  // HTML exportado → SVG inline (vetorial, nítido em qualquer zoom)
  return renderMarkdownToHtml(content, "svg");
}

function buildHtmlDoc(title: string, metaHtml: string, contentHtml: string): string {
  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>${escapeHtml(title)}</title>
<style>${EXPORT_HTML_STYLES}</style>
</head>
<body>
<div class="doc">
  <header class="doc-header">
    <h1>${escapeHtml(title)}</h1>
    <div class="meta">${metaHtml}</div>
  </header>
  ${contentHtml}
  <div class="doc-footer">Gerado pelo ${escapeHtml(getBranding().appName)}</div>
</div>
<script>${EXPORT_LIGHTBOX_SCRIPT}</script>
</body>
</html>`;
}

function triggerHtmlDownload(html: string, filename: string): void {
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

/**
 * Exporta uma única mensagem (turn) como HTML autocontido.
 * Mesmos estilos do export de conversa inteira — consistente visualmente.
 */
export async function exportMessageHtml(turn: Turn): Promise<void> {
  if (!turn.content.trim() && !turn.errored) {
    showToast("Mensagem vazia - nada para exportar.", "error");
    return;
  }
  const exportTs = new Date().toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });
  const body = await renderTurnBody(turn);
  const block = renderTurnBlock(turn, body);
  const role = turn.role === "user" ? "Mensagem" : "Resposta";
  const html = buildHtmlDoc(
    `${role} — ${getBranding().appName}`,
    `Exportada em ${escapeHtml(exportTs)}`,
    block,
  );
  triggerHtmlDownload(html, defaultFilename("html"));
}

/**
 * Exporta a conversa como HTML autocontido (sem CSS/JS externo).
 * Cada turn vira um bloco com markdown renderizado, metadados (timestamp,
 * tokens, custo, duração) e suporte a dark mode via prefers-color-scheme.
 */
export async function exportConversationHtml(turns: Turn[]): Promise<void> {
  if (turns.length === 0) {
    showToast("Conversa vazia - nada para exportar.", "error");
    return;
  }

  const exportTs = new Date().toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });
  const turnBlocks: string[] = [];
  for (const t of turns) {
    const body = await renderTurnBody(t);
    turnBlocks.push(renderTurnBlock(t, body));
  }
  const html = buildHtmlDoc(
    `Conversa — ${getBranding().appName}`,
    `Exportada em ${escapeHtml(exportTs)} · ${turns.length} mensagens`,
    turnBlocks.join("\n  "),
  );
  triggerHtmlDownload(html, defaultFilename("html"));
}

export async function exportConversationPdf(turns: Turn[]): Promise<void> {
  if (turns.length === 0) {
    showToast("Conversa vazia - nada para exportar.", "error");
    return;
  }

  // Constrói um único markdown da conversa inteira e renderiza pra HTML
  const md = turnsToMarkdown(turns);
  const html = await renderMarkdownToHtml(md);

  // Container print-friendly (mesmo padrão do export por mensagem)
  const clip = document.createElement("div");
  clip.setAttribute("aria-hidden", "true");
  clip.style.position = "fixed";
  clip.style.top = "0";
  clip.style.left = "0";
  clip.style.width = "0";
  clip.style.height = "0";
  clip.style.overflow = "hidden";
  clip.style.pointerEvents = "none";
  clip.style.zIndex = "-1";

  const container = document.createElement("div");
  container.style.width = "180mm";
  container.style.padding = "0";
  container.style.background = "#ffffff";
  container.style.color = "#1f1e1d";
  container.style.fontFamily =
    '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
  container.style.fontSize = "12pt";
  container.style.lineHeight = "1.55";
  container.innerHTML = `
    <style>
      .pdf-doc h1, .pdf-doc h2, .pdf-doc h3, .pdf-doc h4 { font-weight: 600; margin: 12pt 0 6pt; page-break-after: avoid; }
      .pdf-doc h1 { font-size: 18pt; }
      .pdf-doc h2 { font-size: 15pt; color: #c15f3c; padding-top: 6pt; border-top: 1pt solid #e8e3da; }
      .pdf-doc h2:first-child { border-top: none; padding-top: 0; }
      .pdf-doc h3 { font-size: 13pt; }
      .pdf-doc p { margin: 0 0 8pt; }
      .pdf-doc em { color: #6b6964; }
      .pdf-doc hr { display: none; }
      .pdf-doc ul, .pdf-doc ol { margin: 0 0 8pt; padding-left: 18pt; }
      .pdf-doc li { margin: 2pt 0; }
      .pdf-doc pre { background: #f4f1ea; padding: 8pt 10pt; border-radius: 4pt; font-family: "JetBrains Mono", ui-monospace, monospace; font-size: 10pt; white-space: pre-wrap; word-wrap: break-word; overflow: hidden; page-break-inside: avoid; }
      .pdf-doc code { background: #f4f1ea; padding: 1pt 4pt; border-radius: 2pt; font-family: "JetBrains Mono", ui-monospace, monospace; font-size: 10pt; color: #c15f3c; }
      .pdf-doc pre code { background: transparent; padding: 0; color: inherit; }
      .pdf-doc blockquote { border-left: 3pt solid #f4dccd; padding-left: 8pt; color: #3d3b38; margin: 6pt 0; }
      .pdf-doc table { border-collapse: collapse; width: 100%; margin: 8pt 0; font-size: 10.5pt; }
      .pdf-doc th, .pdf-doc td { border: 1pt solid #e8e3da; padding: 5pt 7pt; text-align: left; }
      .pdf-doc th { background: #f0ede6; }
      .pdf-doc a { color: #c15f3c; text-decoration: none; }
      .pdf-meta { font-size: 9pt; color: #6b6964; margin-bottom: 12pt; padding-bottom: 8pt; border-bottom: 1pt solid #e8e3da; }
      .pdf-footer { font-size: 8pt; color: #97948d; margin-top: 14pt; padding-top: 8pt; border-top: 1pt solid #e8e3da; }
    </style>
    <div class="pdf-meta">${getBranding().appName} — conversa exportada em ${new Date().toLocaleString(
      "pt-BR",
      { timeZone: "America/Sao_Paulo" },
    )}</div>
    <div class="pdf-doc">${html}</div>
    <div class="pdf-footer">Gerado pelo ${getBranding().appName}</div>
  `;
  clip.appendChild(container);
  document.body.appendChild(clip);

  try {
    container.getBoundingClientRect();
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
    );

    const rect = container.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      throw new Error(`Container sem dimensões (${rect.width}x${rect.height}).`);
    }

    const mod = await import("html2pdf.js");
    const html2pdf =
      (mod as { default?: () => unknown }).default ??
      ((mod as unknown) as () => unknown);

    if (typeof html2pdf !== "function") {
      throw new Error("html2pdf não carregou corretamente.");
    }

    await (html2pdf as () => {
      set: (o: unknown) => { from: (e: HTMLElement) => { save: () => Promise<void> } };
    })()
      .set({
        margin: [15, 15, 15, 15],
        filename: defaultFilename("pdf"),
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["css", "legacy"] },
      })
      .from(container)
      .save();
  } catch (err) {
    console.error("Erro ao exportar conversa em PDF:", err);
    showToast(`Não consegui gerar o PDF da conversa: ${err instanceof Error ? err.message : String(err)}`, "error");
  } finally {
    if (clip.parentNode) clip.parentNode.removeChild(clip);
  }
}
